import { useMemo, useRef, useState } from "react";
import { apiRequest, isAbortError } from "../../lib/apiClient";
import { useCollection } from "../../lib/useCollection";
import { useToast } from "../../components/ui/ToastProvider";
import { useConfirm } from "../../components/ui/ConfirmProvider";
import { AsyncSection } from "../../components/ui/AsyncSection";
import { Pagination } from "../../components/ui/Pagination";
import { TextField, TextAreaField, SelectField, FileField } from "../../components/ui/Field";
import { NEWS_CATEGORIES, NEWS_PAGE_SIZE, toDateInput } from "../../config/constants";
import { FeaturedStoryCard } from "./FeaturedStoryCard";

const EMPTY_FORM = { title: "", body: "", category: "", date: "", link: "" };

export function NewsPanel() {
  const [page, setPage] = useState(1);

  const path = useMemo(
    () => `/api/news?page=${page}&limit=${NEWS_PAGE_SIZE}`,
    [page]
  );

  const { status, data, error, reload } = useCollection(path, (payload) => ({
    items: Array.isArray(payload?.items) ? payload.items : [],
    total: payload?.total ?? 0,
    totalPages: payload?.totalPages ?? 1,
  }));

  const [form, setForm] = useState(EMPTY_FORM);
  const [image, setImage] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  const toast = useToast();
  const confirm = useConfirm();

  const updateField = (field) => (value) => setForm((current) => ({ ...current, [field]: value }));

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImage(null);
    setEditingId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const startEditing = (item) => {
    setEditingId(item._id);
    setForm({
      title: item.title || "",
      body: item.body || "",
      category: item.category || "",
      date: toDateInput(item.date),
      link: item.link || "",
    });
    setImage(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!form.title.trim()) {
      toast.error("A title is required.");
      return;
    }
    if (!form.category) {
      toast.error("Choose a category.");
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => formData.append(key, value));
    if (image) formData.append("image", image);

    setSaving(true);
    try {
      await apiRequest(
        editingId ? `/api/news/update/${editingId}` : "/api/news/add",
        { method: editingId ? "PUT" : "POST", auth: true, body: formData }
      );

      toast.success(editingId ? "News item updated." : "News item published.");
      resetForm();
      reload();
    } catch (err) {
      if (!isAbortError(err)) toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const confirmed = await confirm({
      title: "Delete this news item?",
      message: `“${item.title}” will be removed from the site permanently.`,
    });
    if (!confirmed) return;

    try {
      await apiRequest(`/api/news/delete/${item._id}`, { method: "DELETE", auth: true });
      toast.success("News item deleted.");

      if (editingId === item._id) resetForm();

      // Deleting the last item on a page would strand the user on an empty one.
      if (data.items.length === 1 && page > 1) setPage(page - 1);
      else reload();
    } catch (err) {
      if (!isAbortError(err)) toast.error(err.message);
    }
  };

  return (
    <section className="panel" aria-labelledby="news-heading">
      <FeaturedStoryCard />

      <div className="upload-card" ref={formRef}>
        <h2 id="news-heading">{editingId ? "Edit news item" : "Add news item"}</h2>

        <form onSubmit={handleSave} className="news-form-stack">
          <TextField
            label="Title"
            value={form.title}
            onChange={updateField("title")}
            disabled={saving}
            required
          />

          <TextAreaField
            label="Body"
            value={form.body}
            onChange={updateField("body")}
            disabled={saving}
          />

          <div className="form-row-split">
            <SelectField
              label="Category"
              value={form.category}
              onChange={updateField("category")}
              options={NEWS_CATEGORIES}
              placeholder="Select a category…"
              disabled={saving}
              required
            />

            <TextField
              label="Publish date"
              type="date"
              value={form.date}
              onChange={updateField("date")}
              disabled={saving}
            />
          </div>

          <TextField
            label="Full article URL"
            type="url"
            value={form.link}
            onChange={updateField("link")}
            hint="Optional. Where “Read more” should link to."
            disabled={saving}
          />

          <div className="form-row-split">
            <FileField
              label="Cover image"
              hint="Optional. Maximum 10MB."
              inputRef={fileInputRef}
              onChange={setImage}
              disabled={saving}
            />

            <div className="form-actions">
              <button type="submit" disabled={saving}>
                {saving
                  ? "Saving…"
                  : editingId ? "Update news item" : "Add news item"}
              </button>

              {editingId && (
                <button type="button" className="button-secondary" onClick={resetForm} disabled={saving}>
                  Cancel edit
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      <AsyncSection
        status={status}
        error={error}
        isEmpty={data?.items.length === 0}
        emptyMessage="No news items yet."
        onRetry={reload}
      >
        <>
          <ul className="slides-grid">
            {data?.items.map((item) => (
              <li key={item._id} className="slide-card">
                {item.image ? (
                  <img src={item.image} alt={item.title} loading="lazy" decoding="async" />
                ) : (
                  <div className="no-image-placeholder">No image</div>
                )}

                <h3 className="slide-card__title">{item.title}</h3>

                <div className="slide-card__actions">
                  <button type="button" onClick={() => startEditing(item)}>
                    Edit<span className="visually-hidden">: {item.title}</span>
                  </button>
                </div>

                <button
                  type="button"
                  className="delete-btn"
                  onClick={() => handleDelete(item)}
                  aria-label={`Delete news item: ${item.title}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>

          <Pagination
            page={page}
            totalPages={data?.totalPages ?? 1}
            total={data?.total ?? 0}
            itemLabel="news items"
            onPageChange={setPage}
          />
        </>
      </AsyncSection>
    </section>
  );
}
